import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PermissionGuard from '../components/PermissionGuard.jsx';
import { MODULES, ACTIONS } from '../context/PermissionsContext.jsx';
import api from '../services/api.js';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const CHART_FILTERS = ['ALL', '1M', '6M', '1Y'];

const MONTH_TH = { Jan: 'ม.ค.', Feb: 'ก.พ.', Mar: 'มี.ค.', Apr: 'เม.ย.', May: 'พ.ค.', Jun: 'มิ.ย.', Jul: 'ก.ค.', Aug: 'ส.ค.', Sep: 'ก.ย.', Oct: 'ต.ค.', Nov: 'พ.ย.', Dec: 'ธ.ค.' };

const translateLabel = (label, lang) => {
  if (lang !== 'th' || !label) return label;
  return MONTH_TH[label] || label;
};

const EMPTY_SUMMARY = {
  stats: {},
  payrollSummary: { currentMonth: {}, allTime: {} },
  chart: { all: [], yearly: [], last6Months: [], currentMonthDaily: [], monthly: [] },
  recentWorkers: [],
  pendingLeaveRequests: [],
  todayAttendance: [],
  upcomingHolidays: [],
  departmentCounts: [],
  roleCounts: [],
  recentActivity: []
};

function Dashboard({ lang = 'en', onNavigate }) {
  const [chartFilter, setChartFilter] = useState('1Y');
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const fetchDashboard = useCallback(async ({ silent = false, force = false } = {}) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError('');
      const data = await api.dashboard.getSummary({ refresh: force });
      setSummary({ ...EMPTY_SUMMARY, ...(data || {}) });
    } catch (e) {
      console.error('Error loading dashboard summary:', e);
      setError(e.message || 'Unable to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();

    const intervalId = setInterval(() => {
      fetchDashboard({ silent: true });
    }, 60000);

    return () => clearInterval(intervalId);
  }, [fetchDashboard]);

  const stats = summary.stats || {};
  const chartConfig = useMemo(() => {
    const chart = summary.chart || {};

    if (chartFilter === 'ALL') {
      return {
        data: Array.isArray(chart.all) ? chart.all : [],
        xKey: 'label',
        title: 'All Years'
      };
    }

    if (chartFilter === '1M') {
      return {
        data: Array.isArray(chart.currentMonthDaily) ? chart.currentMonthDaily : [],
        xKey: 'label',
        title: 'Current Month - Daily'
      };
    }

    if (chartFilter === '6M') {
      return {
        data: Array.isArray(chart.last6Months) ? chart.last6Months : [],
        xKey: 'label',
        title: 'Last 6 Months'
      };
    }

    const yearlyData = Array.isArray(chart.yearly) ? chart.yearly : (Array.isArray(chart.monthly) ? chart.monthly : []);
    return {
      data: yearlyData,
      xKey: yearlyData.length > 0 && yearlyData[0].label !== undefined ? 'label' : 'month',
      title: String(chart.year || new Date().getFullYear()) + ' - Monthly'
    };
  }, [summary.chart, chartFilter]);

  const visibleData_raw = chartConfig.data;
  const visibleData = visibleData_raw.map(item => ({ ...item, label: translateLabel(item.label, lang) }));

  const hasChartValues = visibleData.some((item) =>
    Number(item.workers || item.Workers || 0) > 0 ||
    Number(item.leaves || item.Leaves || 0) > 0
  );

  const statCards = [
    {
      label: 'Total Worker/Employee',
      value: stats.totalWorkers || 0,
      icon: 'bi-people-fill',
      trend: '+2.3%',
      trendUp: true,
      sub: 'This Month',
      module: MODULES.DASHBOARD.submodules?.TOTAL_WORKERS || 'total_workers',
      page: 'workers',
    },
    {
      label: 'Present Today',
      value: stats.presentToday || 0,
      icon: 'bi-calendar-check-fill',
      trend: `${stats.absentToday || 0} absent`,
      trendUp: true,
      sub: `${stats.lateToday || 0} late`,
      module: MODULES.DASHBOARD.submodules?.PRESENT_TODAY || 'present_today',
      page: 'attendance',
    },
    {
      label: 'Pending Leaves',
      value: stats.pendingLeaves || 0,
      icon: 'bi-calendar-x-fill',
      trend: `${stats.approvedLeaves || 0} approved`,
      trendUp: false,
      sub: `${stats.rejectedLeaves || 0} rejected`,
      module: MODULES.DASHBOARD.submodules?.PENDING_LEAVES || 'pending_leaves',
      page: 'leave',
    },
    {
      label: 'Departments',
      value: stats.totalDepartments || 0,
      icon: 'bi-building-fill',
      trend: `${summary.roleCounts?.length || 0} roles`,
      trendUp: false,
      sub: 'Active Groups',
      module: MODULES.DASHBOARD.submodules?.TOTAL_WORKERS || 'total_workers',
    },
  ];


  return (
    <PermissionGuard module={MODULES.DASHBOARD} action={ACTIONS.LIST}>
      <div className="dash-wrap">
        <div className="d-flex justify-content-end mb-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => fetchDashboard({ silent: true, force: true })}
            disabled={loading || refreshing}
          >
            <i className={`bi bi-arrow-clockwise me-1 ${refreshing ? 'spin-icon' : ''}`}></i>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {error && (
          <div className="alert alert-danger mt-3 mb-0" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center text-muted py-5">Loading dashboard...</div>
        ) : (
          <>
            <div className="dash-grid">
              {/* LEFT: 2x2 stat cards */}
              <div className="dash-cards">
                {statCards.map((card, idx) => (
                  <PermissionGuard key={idx} module={MODULES.DASHBOARD} action={ACTIONS.VIEW} submodule={card.module}>
                    <div
                      className="dash-stat-card"
                      onClick={() => card.page && onNavigate && onNavigate(card.page)}
                      style={{ cursor: card.page && onNavigate ? 'pointer' : 'default' }}
                    >
                      <div className="dsc-top">
                        <div className="dsc-icon">
                          <i className={`bi ${card.icon}`}></i>
                        </div>
                        <div className="dsc-info">
                          <div className="dsc-label">{card.label}</div>
                          <div className="dsc-value">{Number(card.value || 0).toLocaleString()}</div>
                        </div>
                      </div>
                      <div className="dsc-bottom">
                        <span className={`dsc-trend ${card.trendUp ? 'up' : 'down'}`}>
                          <i className={`bi ${card.trendUp ? 'bi-arrow-up' : 'bi-arrow-down'}`}></i>
                          {card.trend}
                        </span>
                        <span className="dsc-sub">{card.sub}</span>
                        {card.page && (
                          <span
                            className="dsc-view"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (card.page && onNavigate) onNavigate(card.page);
                            }}
                          >
                            View More
                          </span>
                        )}
                      </div>
                    </div>
                  </PermissionGuard>
                ))}
              </div>

              {/* RIGHT: Chart */}
              <PermissionGuard module={MODULES.DASHBOARD} action={ACTIONS.VIEW} submodule="recent_workers">
                <div className="dash-chart-card">
                  <div className="dcc-header">
                    <span className="dcc-title">Performance <small className="text-muted">({chartConfig.title})</small></span>
                    <div className="dcc-filters">
                      {CHART_FILTERS.map(f => (
                        <button
                          key={f}
                          className={`dcc-filter-btn ${chartFilter === f ? 'active' : ''}`}
                          onClick={() => setChartFilter(f)}
                        >{f}</button>
                      ))}
                    </div>
                  </div>
                  {visibleData.length > 0 && hasChartValues ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <ComposedChart data={visibleData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey={chartConfig.xKey} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                        <Tooltip />
                        <Legend iconType="circle" iconSize={8} />
                        <Bar dataKey="workers" name="Workers" fill="#4f46e5" radius={[4, 4, 0, 0]} barSize={22} />
                        <Line dataKey="leaves" name="Leaves" type="monotone" stroke="#10b981" strokeWidth={2} dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center text-muted py-5">No chart data available</div>
                  )}
                </div>
              </PermissionGuard>
            </div>

            {/* Bottom tables - 3 columns */}
            <div className="dash-tables">
              <PermissionGuard module={MODULES.DASHBOARD} action={ACTIONS.VIEW} submodule="recent_workers">
                <div className="dash-table-card">
                  <div className="dtc-header">
                    <i className="bi bi-people-fill"></i> Recent Workers/ Employee
                  </div>
                  <table className="table table-sm dash-table">
                    <thead>
                      <tr><th>Name</th><th>Department</th><th>Shift</th><th>Join Date</th></tr>
                    </thead>
                    <tbody>
                      {Array.isArray(summary.recentWorkers) && summary.recentWorkers.length > 0
                        ? summary.recentWorkers.map(w => (
                          <tr key={w.id}>
                            <td>{w.name || '-'}</td>
                            <td>{w.department || '-'}</td>
                            <td>{w.shiftName || w.shift || '-'}</td>
                            <td>{w.joinDate || '-'}</td>
                          </tr>
                        ))
                        : <tr><td colSpan="4" className="text-center text-muted">No workers found</td></tr>
                      }
                    </tbody>
                  </table>
                </div>
              </PermissionGuard>

              <PermissionGuard module={MODULES.DASHBOARD} action={ACTIONS.VIEW} submodule="pending_leave_requests">
                <div className="dash-table-card">
                  <div className="dtc-header">
                    <i className="bi bi-calendar-x-fill"></i> Pending Leave Requests
                  </div>
                  <table className="table table-sm dash-table">
                    <thead>
                      <tr><th>Worker</th><th>Type</th><th>From</th><th>To</th><th>Days</th></tr>
                    </thead>
                    <tbody>
                      {Array.isArray(summary.pendingLeaveRequests) && summary.pendingLeaveRequests.length > 0
                        ? summary.pendingLeaveRequests.map(l => (
                          <tr key={l.id}>
                            <td>{l.workerName || `Worker ${l.workerId}`}</td>
                            <td>{l.type || '-'}</td>
                            <td>{l.startDt || '-'}</td>
                            <td>{l.endDt || l.startDt || '-'}</td>
                            <td>{l.days ?? '-'}</td>
                          </tr>
                        ))
                        : <tr><td colSpan="5" className="text-center text-muted">No pending leaves</td></tr>
                      }
                    </tbody>
                  </table>
                </div>
              </PermissionGuard>

              <PermissionGuard module={MODULES.DASHBOARD} action={ACTIONS.VIEW} submodule={MODULES.DASHBOARD.submodules?.PRESENT_TODAY || 'present_today'}>
                <div className="dash-table-card">
                  <div className="dtc-header">
                    <i className="bi bi-clock-history"></i> Today's Attendance
                  </div>
                  <table className="table table-sm dash-table">
                    <thead>
                      <tr><th>Worker</th><th>Department</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {Array.isArray(summary.todayAttendance) && summary.todayAttendance.length > 0
                        ? summary.todayAttendance.map(a => (
                          <tr key={a.id}>
                            <td>{a.workerName || `Worker ${a.workerId}`}</td>
                            <td>{a.department || '-'}</td>
                            <td>
                              <span className={`att-badge ${a.status === 0 ? 'present' : 'absent'}`}>
                                {a.statusLabel || (a.status === 0 ? 'Present' : 'Absent')}
                              </span>
                            </td>
                          </tr>
                        ))
                        : <tr><td colSpan="3" className="text-center text-muted">No attendance today</td></tr>
                      }
                    </tbody>
                  </table>
                </div>
              </PermissionGuard>
            </div>
          </>
        )}
      </div>
    </PermissionGuard>
  );
}

export default Dashboard;
